import React from 'react';
import { View, StyleSheet, Image, ScrollView, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTranslation } from 'react-i18next';
import { ProfileData } from '../../services/discoveryOnboardingAPI';
import { completeOnboarding as markOnboardingComplete } from '../../utils/onboardingStorage';
import { useSubscription } from '../../hooks/useSubscription';
import { 
  Text, 
  Button,
  OnboardingPageContainer
} from '../../components/ui';
import AppHeader from '../../components/ui/AppHeader';
import ProfileSummaryItem from '../../components/ui/ProfileSummaryItem';
import { Theme } from '../../utils/theme';

interface ProfileSummaryScreenProps {
  route: {
    params: {
      profile: ProfileData;
      userName: string;
    };
  };
}

const ProfileSummaryScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { t } = useTranslation();
  const { profile, userName } = route.params as ProfileSummaryScreenProps['route']['params'];
  const { isFree } = useSubscription();

  const handleStartGrowing = async () => {
    await markOnboardingComplete();
    // Force navigation to Main dashboard, with paywall for free users (mobile only)
    if (isFree && Platform.OS !== 'web') {
      (navigation as any).reset({
        index: 1,
        routes: [{ name: 'Main' }, { name: 'Paywall' }],
      });
      return;
    }
    (navigation as any).reset({
      index: 0,
      routes: [{ name: 'Main' }],
    });
  };

  // Icon mapping function
  const getIconForLabel = (label: string) => {
    const iconProps = { size: 24, color: Theme.colors.primary };
    
    const labelKey = label.toLowerCase().replace(/\s+/g, '');
    const labelMap: Record<string, string> = {
      'primaryfocus': 'target',
      'focus': 'target',
      'preferredmentors': 'people',
      'guidestyle': 'people',
      'coremotivation': 'heart',
      'mainmotivation': 'heart',
      'mainchallenge': 'warning',
      'biggestchallenge': 'warning',
      'learningstyle': 'time',
      'speedoflearning': 'time',
      'keystrength': 'trophy',
      'primarygoal': 'trophy',
    };
    
    const iconName = labelMap[labelKey] || 'information-circle';
    return <Ionicons name={iconName as any} {...iconProps} />;
  };

  return (
    <SafeAreaView style={styles.container}>
      <OnboardingPageContainer>
        <View style={styles.content}>
          <AppHeader showBackButton />
          
          <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
          <View style={styles.mainContent}>
            {/* Avatar Image */}
            <Image 
              style={styles.avatarImage} 
              resizeMode="cover" 
              source={require('../../assets/mentor/mentor-default.webp')} 
            />
            
            {/* Summary Container */}
            <View style={styles.summaryContainer}>
              <View style={styles.headerContainer}>
                <Text variant="heading2" style={styles.title}>
                  {t('profileSummary.title')}
                </Text>
              </View>
              
              {/* Name Item */}
              <ProfileSummaryItem
                icon={<Ionicons name="person" size={24} color={Theme.colors.primary} />}
                label={t('profileSummary.labels.name')}
                value={userName}
                isName
              />
              
              {/* Profile Summary Items */}
              <View style={styles.summaryItems}>
                {profile.profileSummary.map((item, index) => (
                  <ProfileSummaryItem
                    key={index}
                    icon={getIconForLabel(item.label)}
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </View>
            </View>
          </View>
        </ScrollView>
        
        {/* Bottom Button */}
        <View style={styles.footer}>
          <Button
            title={t('profileSummary.button')}
            onPress={handleStartGrowing}
            variant="primary"
          />
        </View>
      </View>
      </OnboardingPageContainer>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  scrollContainer: {
    flex: 1,
  },
  mainContent: {
    padding: Theme.spacing.xxl,
    gap: Theme.spacing.xxl,
    alignItems: 'center',
  },
  avatarImage: {
    width: 128,
    height: 199,
  },
  summaryContainer: {
    backgroundColor: Theme.colors.primaryLight,
    paddingVertical: Theme.spacing.xxxl,
    paddingHorizontal: Theme.spacing.xxl,
    gap: Theme.spacing.sm,
    alignItems: 'center',
    alignSelf: 'stretch',
    borderRadius: Theme.borderRadius.lg,
  },
  headerContainer: {
    alignSelf: 'stretch',
  },
  title: {
    color: Theme.colors.textPrimary,
    fontFamily: Theme.typography.fontFamily.semiBold,
    textAlign: 'left',
    flex: 1,
  },
  summaryItems: {
    paddingHorizontal: 0,
    paddingVertical: Theme.spacing.sm,
    gap: Theme.spacing.sm,
    alignSelf: 'stretch',
    width: '100%',
  },
  footer: {
    backgroundColor: 'rgba(255, 255, 255, 0.01)',
    paddingHorizontal: Theme.spacing.xxl,
    paddingTop: Theme.spacing.sm,
    paddingBottom: Theme.spacing.xl,
  },
});

export default ProfileSummaryScreen; 